// Feature visibility per role (GET /access/features). Loaded once per signed-in
// user and role; the shell filters its rows with it and the router shows a
// clear "Fitur tidak tersedia" state on a route whose feature is off.
//
// Until the answer arrives (or when the call fails) every feature counts as
// visible: the API enforces the matrix on its own (403 feature_disabled), so
// an optimistic menu never opens anything the server would serve.
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import * as API from "../api";
import type { Feature, User } from "../api";
import { isActiveAccount, PAID_FEATURES } from "../lib/account";

export interface FeatureState {
  /** null while loading or after a failure: treat as all visible. */
  list: Feature[] | null;
  has: (f: Feature | undefined) => boolean;
}

const ALL: FeatureState = { list: null, has: () => true };

const FeatureContext = createContext<FeatureState>(ALL);

export function useFeatures(): FeatureState {
  return useContext(FeatureContext);
}

export function FeatureProvider({ user, children }: { user: User | null; children: ReactNode }) {
  const [list, setList] = useState<Feature[] | null>(null);
  const key = user && user.role !== "pending" && user.role !== "admin" ? `${user.id}:${user.role}` : null;

  useEffect(() => {
    setList(null);
    if (!key) return;
    let alive = true;
    API.getFeatures().then(
      (features) => alive && setList(features),
      () => alive && setList(null),
    );
    return () => {
      alive = false;
    };
  }, [key]);

  // A non-active account (waiting for activation) keeps plain chat only.
  const locked = !!user && user.role !== "pending" && !isActiveAccount(user);
  let value: FeatureState = list === null ? ALL : { list, has: (f) => !f || list.includes(f) };
  if (locked) {
    const base = value;
    value = { list: (list ?? []).filter((f) => !PAID_FEATURES.includes(f)), has: (f) => !f || (!PAID_FEATURES.includes(f) && base.has(f)) };
  }
  return <FeatureContext.Provider value={value}>{children}</FeatureContext.Provider>;
}
